import React, { useState } from 'react';
import axios from 'axios';
import './PaymentLookup.css';
import API_BASE_URL from '../config';

function PaymentLookup() {
    const [identifier, setIdentifier] = useState('');
    const [installments, setInstallments] = useState('12'); // Default to 12
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null); // { type: 'qr' | 'period', data: any }

    const [showDebug, setShowDebug] = useState(false);

    const handleInputChange = (e) => {
        setIdentifier(e.target.value);
        if (error) setError('');
    };

    const handleInstallmentChange = (e) => {
        const val = e.target.value;
        if (/^\d*$/.test(val)) {
            setInstallments(val);
        }
    };

    const getQRCode = async () => {
        if (!identifier.trim()) {
            setError('กรุณาระบุเลขที่ใบจอง (PO), เลขที่รับแจ้ง (Noti No.) หรือ Transaction ID');
            return;
        }

        setLoading(true);
        setError('');
        setResult(null);

        try {
            // Determine if input is PO/Noti or Transaction ID
            const isTransactionId = /^\d+$/.test(identifier.trim()) && identifier.trim().length < 10;

            const payload = {
                payment_mode: 'CURRENT' // Default to current installment
            };

            if (isTransactionId) {
                payload.transaction_id = identifier.trim();
            } else {
                payload.noti_number = identifier.trim().toUpperCase();
            }

            const response = await axios.post(`${API_BASE_URL}/api/po/qr-payment`, payload);

            if (response.data.success) {
                setResult({
                    type: 'qr',
                    data: response.data.data,
                    debug: { payload, response: response.data }
                });
            } else {
                setError(response.data.error || 'ไม่สามารถดึง QR Code ได้ กรุณาตรวจสอบข้อมูล');
                setResult({ type: 'error', debug: { payload, response: response.data } });
            }
        } catch (err) {
            console.error('Error fetching QR:', err);
            setError(err.response?.data?.error || err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ');
        } finally {
            setLoading(false);
        }
    };

    const checkPeriodStatus = async () => {
        if (!identifier.trim()) {
            setError('กรุณาระบุเลขที่ใบจอง (PO), เลขที่รับแจ้ง (Noti No.) หรือ Transaction ID');
            return;
        }

        setLoading(true);
        setError('');
        setResult(null);

        try {
            // Determine if input is PO/Noti or Transaction ID
            const isTransactionId = /^\d+$/.test(identifier.trim()) && identifier.trim().length < 10;

            const payload = {
                transaction_id: isTransactionId ? parseInt(identifier.trim()) : null,
                noti_number: isTransactionId ? null : identifier.trim().toUpperCase(),
                number_installment: parseInt(installments) || 12
            };

            const endpoint = `${API_BASE_URL}/api/po/period-status`;
            const response = await axios.post(endpoint, payload);

            if (response.data.success) {
                setResult({
                    type: 'period',
                    data: response.data.data,
                    debug: { endpoint, payload, response: response.data }
                });
            } else {
                setError(response.data.error || 'ไม่สามารถตรวจสอบสถานะงวดได้');
                setResult({ type: 'error', debug: { endpoint, payload, response: response.data } });
            }
        } catch (err) {
            console.error('Error fetching period status:', err);
            setError(err.response?.data?.error || err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('th-TH', {
            style: 'currency',
            currency: 'THB'
        }).format(amount);
    };

    return (
        <div className="payment-lookup">
            <h2>Payment Lookup (ค้นหาข้อมูลชำระเงิน)</h2>

            <div className="lookup-form">
                <div className="form-group">
                    <label htmlFor="identifier">PO Number / Noti Number / Transaction ID</label>
                    <input
                        id="identifier"
                        type="text"
                        value={identifier}
                        onChange={handleInputChange}
                        placeholder="ระบุเลขที่ใบจอง (PO), Noti No. หรือ Transaction ID"
                        disabled={loading}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="installments">Total Installments (จำนวนงวดทั้งหมด)</label>
                    <input
                        id="installments"
                        type="text"
                        value={installments}
                        onChange={handleInstallmentChange}
                        placeholder="เช่น 10, 12"
                        disabled={loading}
                    />
                </div>

                <div className="button-group">
                    <button
                        className="btn-primary"
                        onClick={getQRCode}
                        disabled={loading || !identifier.trim()}
                    >
                        {loading && result?.type === 'loading-qr' ? 'Loading...' : '📷 Get QR Payment'}
                    </button>
                    <button
                        className="btn-secondary"
                        onClick={checkPeriodStatus}
                        disabled={loading || !identifier.trim() || !installments}
                    >
                        {loading && result?.type === 'loading-period' ? 'Loading...' : '📅 Check Period Status'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    ❌ {error}
                </div>
            )}

            {result && (
                <div className="result-section">
                    <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                        <button
                            onClick={() => setShowDebug(!showDebug)}
                            style={{
                                background: 'transparent',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                color: '#666'
                            }}
                        >
                            {showDebug ? 'Hide Debug' : 'Show Debug'}
                        </button>
                    </div>

                    {showDebug && (
                        <div style={{
                            background: '#f1f3f5',
                            padding: '12px',
                            borderRadius: '4px',
                            marginBottom: '20px',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            overflowX: 'auto'
                        }}>
                            <strong>Endpoint:</strong>
                            <pre>{result.debug?.endpoint || 'N/A'}</pre>
                            <strong>Request Payload:</strong>
                            <pre>{JSON.stringify(result.debug?.payload || {}, null, 2)}</pre>
                            <strong>Response Data:</strong>
                            <pre>{JSON.stringify(result.debug?.response || {}, null, 2)}</pre>
                        </div>
                    )}

                    {/* QR Code Result */}
                    {result.type === 'qr' && (
                        <div className="qr-container">
                            <h3>QR Code Payment</h3>
                            {result.data.qr_path ? (
                                <>
                                    <img
                                        src={result.data.qr_path}
                                        alt="QR Payment"
                                        className="qr-image"
                                    />
                                    <div className="qr-info">
                                        <div className="qr-amount">{formatCurrency(result.data.amount)}</div>
                                        <div>Ref 1: {result.data.reference1 || '-'}</div>
                                        <div>Ref 2: {result.data.reference2 || '-'}</div>
                                    </div>
                                </>
                            ) : (
                                <div className="error-message">ไม่พบรูปภาพ QR Code ในระบบ</div>
                            )}
                        </div>
                    )}

                    {/* Period Status Result */}
                    {result.type === 'period' && (
                        <div className="period-container">
                            <h3>Installment Status (สถานะการผ่อนชำระ)</h3>
                            {(!result.data || result.data.length === 0) ? (
                                <div className="no-data">ไม่พบข้อมูลการผ่อนชำระ</div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="period-table">
                                        <thead>
                                            <tr>
                                                <th>งวดที่</th>
                                                <th>วันครบกำหนด</th>
                                                <th>วันที่รับชำระ</th>
                                                <th>ยอดเงิน</th>
                                                <th>ค่าธรรมเนียม</th>
                                                <th>สถานะ</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(result.data) && result.data.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.period_no || item.period || index + 1}</td>
                                                    <td>{item.payment_due_date || item.due_date || '-'}</td>
                                                    <td>{item.payment_received_date || item.paid_date || '-'}</td>
                                                    <td className="price">{formatCurrency(item.amount)}</td>
                                                    <td className="price">{item.fine ? formatCurrency(item.fine) : '-'}</td>
                                                    <td>
                                                        <span className={`status-badge ${(item.status === 'Paid')
                                                            ? 'status-paid'
                                                            : (item.status === 'ERROR' ? 'status-overdue' : 'status-pending')
                                                            }`}>
                                                            {item.status || 'Pending'}
                                                        </span>
                                                        {item.error_message && (
                                                            <div style={{ fontSize: '0.8em', color: 'red', marginTop: '4px' }}>
                                                                {item.error_message}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default PaymentLookup;
